import { useContext, useEffect, useRef, useState } from "react";
import AppContext from "../../context";

const SUMUP_SCRIPT_URL = "https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js";

const loadSumupScript = () => {
	return new Promise((resolve, reject) => {
		if (window.SumUpCard) {
			resolve();
			return;
		}

		const existing = document.querySelector(`script[src="${SUMUP_SCRIPT_URL}"]`);
		if (existing) {
			existing.addEventListener("load", resolve);
			existing.addEventListener("error", reject);
			return;
		}

		const script = document.createElement("script");
		script.src = SUMUP_SCRIPT_URL;
		script.async = true;
		script.onload = resolve;
		script.onerror = reject;
		document.body.appendChild(script);
	});
};

function SumupWidget({ checkoutId, onSuccess, onError }) {
	const context = useContext(AppContext);
	const domain = context?.domain;
	const containerRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const widgetIdRef = useRef("sumup-card");

	useEffect(() => {
		let cancelled = false;

		const mountWidget = async () => {
			setLoading(true);
			setLoadError(null);

			try {
				await loadSumupScript();
				if (cancelled) return;

				if (!window.SumUpCard || !containerRef.current) {
					throw new Error("SumUp widget failed to load.");
				}

				containerRef.current.innerHTML = "";
				containerRef.current.id = widgetIdRef.current;
				const mountPayload = {
					checkoutId,
					elementId: widgetIdRef.current,
					element: containerRef.current,
					onPaymentSuccess: () => {
						onSuccess?.();
					},
					onPaymentFail: (message) => {
						onError?.(message || "Payment failed");
					},
					onError: (error) => {
						onError?.(error?.message || "Payment error");
					}
				};

				if (domain?.googlePayEnabled && domain?.googlePayMerchantId) {
					mountPayload.googlePay = {
						merchantId: domain.googlePayMerchantId,
						merchantName: domain.name
					};
				}

				window.SumUpCard.mount(mountPayload);
			} catch (err) {
				setLoadError(err?.message || "Failed to load SumUp widget.");
				onError?.(err);
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		if (checkoutId) {
			mountWidget();
		}

		return () => {
			cancelled = true;
		};
	}, [checkoutId, domain, onError, onSuccess]);

	if (!checkoutId) return null;

	return (
		<div>
			{loading && <p>Loading payment widget...</p>}
			{loadError && <p style={{ color: "red" }}>{loadError}</p>}
			<div ref={containerRef} />
		</div>
	);
}

export default SumupWidget;
